import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { ACTIONS } from "./Constants";
import Table, { CellTmpl, DEFAULT_PAYLOAD, reactFormatter } from "./Table";
import TableFilters from "./TableFilter";
import Icon from "./Icon";
import { hasUserAccess } from "../../backend/auth";
import ConfirmModal from "./ConfirmModal";
import { ERROR_MESSAGES, SEARCH_PLACEHOLDER } from "../../utils/constants";
import { downloadFileContent } from "../../utils/common";
import PageLoader from "../shared/PageLoader";
import type { TableData } from "../../models/table_data";
import type { FilterPayload } from "../../models/filter_payload";
import { USER_PRIVILEGES } from "../pages/UserManagement/Roles/RoleConfiguration";
import TableActions, { ActionButton } from "./TableActions";
import MastersLayout from "../pages/Masters/MastersLayout";


interface BaseListProps {
  modelSchema: any;
  DetailView: React.ComponentType<any>;
  FormView: React.ComponentType<any>;
  nameAttribute?: string;
  fetchData: any;
  deleteData?: any;
  exportData?: any;
  ImportComponent?: React.ComponentType<any>;
  columns: any[];
  initialPayload?: Record<string, any>;
  initialSort?: string[];
  filterConfig?: { label: string; name: string; options: any; }[],
  breadcrumb?: any[];
  searchConfig?: {label: string, name: string}
  title?: string;
}

const BaseList = ({
  modelSchema,
  DetailView,
  FormView,
  fetchData,        // async function to fetch data (params) => Promise<{ data, total }>
  deleteData,        // async function to fetch data (params) => Promise<{ data, total }>
  exportData,
  ImportComponent,
  columns,          // AntD or custom table column config
  initialPayload = {}, // optional default query params
  initialSort = [], // optional initial sorts array
  filterConfig,
  searchConfig,
  breadcrumb,
  title = `Masters - ${modelSchema.name}`,
}: BaseListProps) => {
  const [t] = useState(new Date().getTime());

  const [data, setData] = useState<TableData | undefined>(undefined);

  const [payload, setPayload] = useState<FilterPayload>({ 
    ...DEFAULT_PAYLOAD,
    ...initialPayload,
    sorts: initialSort, 
    t 
  });
  
  const modelName:string = modelSchema.name;

  const nameAttribute = modelSchema.nameAttribute;

  const { records, total, isFetching, refetch } = fetchData(payload, Boolean(payload));
  
  const [action, setAction] = useState(ACTIONS.NONE);

  const [record, setRecord] = useState<any>(null);

  const { deleteRecord, deleting } = deleteData((response: any) => {
    const recordName = record && record[nameAttribute] ? record[nameAttribute] : 'Record';
    toast.success(recordName + " deleted successfully.");
    submitCallback();
  }, () => {
    toast.error(ERROR_MESSAGES.DEFAULT);
  });
  
  const { exportRecords, exporting } = exportData ? exportData((response: any) => {
    const contentDisposition = response.headers['content-disposition'];
    const matches = /filename="(.+)"/.exec(contentDisposition);
    const filename = (matches ? matches[1] : `${modelSchema.name}.xlsx`);
    downloadFileContent({
      name: filename,
      type: response.headers['content-type'],
      content: response.data
    });
  }, () => {
      toast.error(ERROR_MESSAGES.DEFAULT);
  }) : { exportRecords: null, exporting: false };


  function handleExport() {
    exportRecords({ ...payload, pagination: null });
  }

  // Type-safe component for action column
  const ActionColumnElements =({ cell }: any) => {
    // Safe access to avoid runtime errors
    if (!cell || typeof cell.getData !== 'function') {
      return <div>No actions available</div>;
    }
    
    const row = cell.getData();

    return (
      <div className="d-flex flex-row align-items-center position-relative h-100">
        {
          hasUserAccess(USER_PRIVILEGES[`EDIT_${modelName.toUpperCase()}` as keyof typeof USER_PRIVILEGES]) &&
          <Icon 
            className="mx-2" 
            type="button" 
            name={'pencil'} 
            text={'Edit'} 
            data={row} 
            action={() => {
              setRecord(row);
              setAction(ACTIONS.EDIT);
            }} 
          />
        }
        {/* Delete functionality commented out but kept for future reference 
            {
                hasUserAccess(USER_PRIVILEGES[`DELETE_${modelName.toUpperCase()` as keyof typeof USER_PRIVILEGES]) &&
                <Icon className="mx-2" type="button" name={'trash'} text={'Delete'} data={row} action={() => {
                    setRecord(row);
                    setAction(ACTIONS.DELETE)
                }} />
            }
        */}
        <Icon 
          className="mx-2" 
          type="button" 
          name={'eye'} 
          text={'View'} 
          data={row} 
          action={() => {
            setRecord(row);
            setAction(ACTIONS.VIEW);
          }} 
        />
      </div>
    );
  }

  const action_columns = [
    {
      title: "Actions", hozAlign: "center", width: 140,
      headerSort: false, formatter: reactFormatter(<ActionColumnElements />)
    }
  ]

  const tableColumns = [...columns,...action_columns]

  // Memoize table configuration to prevent unnecessary re-renders
  const tableConfig = useMemo(() => ({
    paginationMode: 'remote',
    ajaxRequestFunc,
    columns: tableColumns,
    rowHeight: 54,
    selectable: false,
    paginate: true,
    initialSort: initialSort
  }), []);

  // Optimize data formatting with memoization
  const formatApiResponse = useCallback((list: any[] = [], totalRecords: number = 0): TableData => {
    const { per = DEFAULT_PAYLOAD.per, page = DEFAULT_PAYLOAD.page } = payload || {};
    return {
      data: list || [],
      total: totalRecords || 0,
      last_page: Math.ceil((totalRecords || list.length) / per) || 1,
      page: page
    };
  }, [payload]);

  // Optimize AJAX request function
  function ajaxRequestFunc(url: any, config: any, pagination_params: any) {
    const { field, dir } = (pagination_params.sort || [])[0] || {};
    // Add SortFields declaration if available in props
    const sort_column = field || 'name';
    const sort_order = dir || 'asc';
    const _params = {
      per: pagination_params.size || DEFAULT_PAYLOAD.per,
      page: pagination_params.page || DEFAULT_PAYLOAD.page,
      sorts: [`${sort_column} ${sort_order}`]
    };
    
    setPayload((prev) => ({
      ...prev, 
      ..._params 
    }));
    
    // Return data if available, otherwise empty formatted data
    return Promise.resolve(data || formatApiResponse(records || [], total || 0));
  }

  // Optimize callback functions with useCallback
  const submitCallback = useCallback(() => {
    setAction(ACTIONS.NONE);
    setRecord(null);
    refetch();
  }, [refetch]);

  const onFilterChange = useCallback((e: { search: string, filters: any }) => {
    const filters = e.filters || {};
    if(!!e.search && e.search != '') {
      if(searchConfig && searchConfig.name) {
        filters[`${searchConfig.name}_cont`]=e.search
      }else {
        filters['name_cont'] = e.search;
      }
      console.log(filters)
    }
    setPayload((prev) => ({ 
      ...prev, 
      page: 1, // Reset to page 1 when searching
      filters: filters
    }));
  }, []);

  const handlePageNav = useCallback((_pagination: { page: number, per: number }) => {
    setPayload((prev) => ({ ...prev, ..._pagination }));
  }, []);

  // Update data when API response changes
  useEffect(() => {
    const formattedData = formatApiResponse(records || [], total || 0);
    setData(formattedData);
  }, [isFetching, records, total, formatApiResponse]);

  const buttons: ActionButton[] = [{
      label: 'Add New',
      name: 'addNew',
      privilege: USER_PRIVILEGES[`ADD_${modelName.toUpperCase()}` as keyof typeof USER_PRIVILEGES],
      icon: 'plus',
      action: () => setAction(ACTIONS.ADD)
  }, {
      label: 'Export',
      name: 'export',
      privilege: USER_PRIVILEGES[`EXPORT_${modelName.toUpperCase()}` as keyof typeof USER_PRIVILEGES],
      icon: 'download',
      disabled: !total,
      action: handleExport
  }, {
      label: 'Import',
      name: 'import',
      privilege: ImportComponent ? USER_PRIVILEGES[`ADD_${modelName.toUpperCase()}` as keyof typeof USER_PRIVILEGES] : "",
      icon: 'upload',
      action: () => setAction(ACTIONS.IMPORT)
  }];

  return (
    <>
      <MastersLayout title={title} breadcrumbs={breadcrumb}>
        <div className="d-flex flex-column mx-0">
            <div className="card shadow d-flex flex-row justify-content-center m-3 p-3">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-end">
                        <TableFilters filterConfig={filterConfig} search={Boolean(SEARCH_PLACEHOLDER[`${modelName.toUpperCase()}`])} onFilterChange={onFilterChange}
                            placeholder={SEARCH_PLACEHOLDER[`${modelName.toUpperCase()}`]} />
                        <TableActions buttons={buttons} />
                    </div>
                </div>
            </div>
            <Table data={data} options={tableConfig} isLoading={isFetching} onPageNav={handlePageNav} />
        </div>          
      </MastersLayout>
      {
          action === ACTIONS.VIEW && record &&
          <DetailView record={record}
              onClose={() => setAction(ACTIONS.NONE)} onSubmit={submitCallback} />
      }
      {
          (action === ACTIONS.ADD || action === ACTIONS.EDIT) &&
          <FormView record={record} onClose={() => {setRecord(null); setAction(ACTIONS.NONE)}} onSubmit={submitCallback}
              action={action} />
      }
      {
          action === ACTIONS.DELETE && record &&
          <ConfirmModal title={`Delete ${modelName}`} onSubmit={() => deleteRecord(record.id)} onClose={() => setAction(ACTIONS.NONE)}>
              <div className="text-center mb-4">Are you sure you want to delete the `${modelName}`, <strong>{record[nameAttribute]}</strong> ?</div>
          </ConfirmModal>
      }
      {
          ImportComponent && action === ACTIONS.IMPORT &&
          <ImportComponent onSubmit={refetch} onClose={() => setAction(ACTIONS.NONE)} />
      }
      {
          deleting && <PageLoader>`Deleting ${modelName}...`</PageLoader>
      }
      {
          exporting && <PageLoader>Preparing Data...</PageLoader>
      }
    </>
  )
};

export default BaseList;

